import { queryAllVideoGroups, queryAllVideoLessons } from '@/lib/dato-request';
import { useAnalytics } from '@/analytics/AnalyticsProvider';
import { SeoVideoEnded, SeoVideoPaused, SeoVideoPlaying, SeoVideoSeeked } from '@/analytics/events';
import dynamic from 'next/dynamic';
import { Layout } from '@/components/Layout';
import { NextSeo } from 'next-seo';
import Head from 'next/head';
import React, { useState, useRef } from 'react';
import EmailSignupField from '@/components/HomePageV2/EmailSignupField/EmailSignupField';

const NoSSRVideoCard = dynamic(() => import('./components/VideoCard'), { ssr: false });
const ReactPlayer = dynamic(() => import('react-player/lazy'), { ssr: false });

// Modal component that appears after 40 seconds
const VideoTimeModal = ({ isOpen, onClose }) => {
    if (!isOpen) return null;

    return (
        <div className='fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center'>
            <div className='bg-white p-6 rounded-xl shadow-lg max-w-md w-full relative min-w-[620px]'>
                {/* Close button */}
                <button 
                    onClick={onClose} 
                    className='absolute top-3 right-3 bg-gray-200 hover:bg-gray-300 text-gray-700 hover:text-gray-900 rounded-full p-1.5 border-[0px] border-solid border-gray-300 transition-colors duration-200 flex items-center justify-center' 
                    aria-label='Close'
                >
                    <svg 
                        xmlns='http://www.w3.org/2000/svg' 
                        className='h-5 w-5' 
                        viewBox='0 0 20 20' 
                        fill='currentColor'
                    >
                        <path 
                            fillRule='evenodd' 
                            d='M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z' 
                            clipRule='evenodd' 
                        />
                    </svg>
                </button>
                <h2 className='text-xl font-bold mb-4'>Want to see more?</h2>
                <p className='mb-4'>Try Muzology free and access our full video library.</p>
                <div className=' mt-4'>
                    <EmailSignupField
                        className='flex-col md:flex-row  flex justify-center md:justify-start items-center gap-4 mt-4'
                        borderColor='black'
                        textColor='black'
                        setRedirectModal={null}
                        setInactiveAccount={null}
                    />
                </div>
            </div>
        </div>
    );
};

const VideoHeaderComponent = ({ video }) => {
    const analytics = useAnalytics();
    const videoId = video?.id;
    const videoTitle = video?.id;
    const [showModal, setShowModal] = useState(false);
    const [isPlaying, setIsPlaying] = useState(true);
    const playerRef = useRef(null);

    const handlePlaying = (...x) => {
        console.log('video playing', x);
        setIsPlaying(true);
        analytics.track(SeoVideoPlaying, { videoId, videoTitle });
    };

    const handlePause = (...x) => {
        console.log('video paused', x);
        setIsPlaying(false);
        analytics.track(SeoVideoPaused, { videoId, videoTitle });
    };

    const handleSeek = (seconds) => {
        console.log('video seeked', seconds);
        analytics.track(SeoVideoSeeked, { videoId, videoTitle, seconds });
    };

    const onEnded = (...x) => {
        console.log('video ended', x);
        analytics.track(SeoVideoEnded, { videoId, videoTitle });
    };

    const handleProgress = (state) => {
        // Check if playback time has reached 40 seconds
        if (state.playedSeconds >= 40 && !showModal) {
            setIsPlaying(false);
            setShowModal(true);
        }
    };

    const closeModal = () => {
        setShowModal(false);
    };

    return (
        <section className='w-full py-4 md:py-10 px-4 md:px-10  '>
            <div className='flex-col-reverse lg:flex-row flex mx-auto max-w-[1400px] md:pt-10 mt-20 '>
                {/* Video Info Section */}
                <div className='h-full w-full lg:w-1/2 py-8 flex flex-col justify-center text-white'>
                    <h1 className='text-3xl font-bold mb-4 mt-0 text-center md:text-left'>{video.title}</h1>
                    <p className='text-xl md:max-w-[410px] text-center md:text-left mt-0'>{video.description}</p>
                    <div className='mt-0 sm:mt-[30px] '>
                        <EmailSignupField
                            className='flex-col md:flex-row  flex justify-center md:justify-start items-center gap-4 mt-4'
                            setRedirectModal={null}
                            setInactiveAccount={null}
                        />
                    </div>
                </div>

                {/* Video Player Section */}
                <div className='w-full lg:w-1/2 lg:max-h-[370px] aspect-video relative mt-5 md:m-auto py-0 md:py-7'>
                    <ReactPlayer
                        ref={playerRef}
                        style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0
                        }}
                        config={{
                            vimeo: {
                                playerOptions: {
                                    playsinline: true
                                }
                            }
                        }}
                        playsinline={true}
                        playing={isPlaying}
                        url={video.videoUrl}
                        width='100%'
                        height='100%'
                        controls={true}
                        muted={true}
                        volume={0}
                        onPlay={handlePlaying}
                        onPause={handlePause}
                        onSeek={handleSeek}
                        onEnded={onEnded}
                        onProgress={handleProgress}
                        progressInterval={1000} // Check progress every second
                    />
                </div>
            </div>

            {/* Modal that appears after 40 seconds */}
            <VideoTimeModal isOpen={showModal} onClose={closeModal} />
        </section>
    );
};

const RelatedVideos = ({ video }) => {
    // randomize and slice the related video arr
    const getRandomItems = (arr) => {
        return arr
            .sort(() => 0.5 - Math.random()) // Shuffle the array
            .slice(0, 3); // Select the first 3 items
    };
    const randomRelatedVideos = getRandomItems(video.relatedVideos);
    console.log('🚀 ~ RelatedVideos ~ randomRelatedVideos:', randomRelatedVideos);

    return (
        <div className=' py-10 px-4 md:px-10'>
            <div className='w-full text-white text-center text-2xl font-semibold pb-3'>Related Videos</div>
            <div className='flex-col-reverse md:flex-row flex items-center justify-center mx-auto max-w-[1400px]'>
                {randomRelatedVideos.map((v) => (
                    <div key={v.id}>
                        <NoSSRVideoCard video={v} />
                    </div>
                ))}
            </div>
        </div>
    );
};

const VideoPage = ({ data }) => {
    const { videoPage, groups } = data;
    const { seo, components } = videoPage;
    return (
        // <Layout showClassCodeInput={false} videoGroups={groups}>
        <Layout showClassCodeInput={false}>
            <NextSeo title={seo?.title ?? videoPage.title ?? videoPage.slug} description={seo?.description} />
            <Head>
                <meta name='keywords' content={videoPage.keywords} />
            </Head>
            <div className='bg-home-background'>
                <VideoHeaderComponent video={videoPage} />
                <RelatedVideos video={videoPage} />
            </div>
        </Layout>
    );
};

export async function getStaticPaths() {
    const query = await queryAllVideoLessons();
    const videos = query.allVideoLessons;
    console.log('videos', videos);
    // Only include valid, non-empty string slugs
    const paths = videos
        .filter(video => typeof video.slug === 'string' && video.slug.trim().length > 0)
        .map(video => ({
            params: {
                slug: video.slug
            }
        }));
    return { paths, fallback: false };
}

export async function getStaticProps({ params }) {
    const query = await queryAllVideoLessons();
    const videos = query.allVideoLessons;
    const video = videos.find((video) => video.slug === params.slug || video.videoId === params.slug);
    const query2 = await queryAllVideoGroups();
    const groups = query2.allVideoGroups;
    const data = {
        videoPage: {
            seo: video.seo,
            components: [],
            ...video
        },
        groups
    };
    return { props: { data } };
}

export default VideoPage;
